import React, { useMemo, useState, useEffect } from 'react';
import './DesktopGroupView.css';

function DesktopGroupView({
  group,
  donors,
  savedLists = [],
  savedTables = [],
  onSelectPerson,
  onSelectList,
  onSelectTable,
  onDeleteList,
  onShowDeleteConfirm,
  onClose,
  onEditContext,
  onCreateList,
  onCreateTable,
  onAddPerson
}) {
  const [activeTab, setActiveTab] = useState(group?.initialTab || 'members'); // 'members', 'lists', 'tables'

  // Update tab when navigating back from a list/table
  useEffect(() => {
    if (group?.initialTab) {
      setActiveTab(group.initialTab);
    }
  }, [group?.initialTab]);

  // Get lists for this group
  const groupLists = useMemo(() => {
    return savedLists.filter(list =>
      list.groupId === group.id || list.group_id === group.id
    );
  }, [savedLists, group.id]);

  // Get tables for this group
  const groupTables = useMemo(() => {
    return savedTables.filter(table =>
      table.groupId === group.id || table.group_id === group.id
    );
  }, [savedTables, group.id]);

  // Helper to check if person is in a group (supports multi-group via group_ids)
  const isPersonInGroup = (person, groupId) => {
    // Normalize to string for comparison (handles int vs string mismatch)
    const targetId = String(groupId);

    // Check group_ids array first (multi-group) - but only if it has items
    if (person.group_ids && Array.isArray(person.group_ids) && person.group_ids.length > 0) {
      return person.group_ids.some(id => String(id) === targetId);
    }
    // Fall back to single group_id
    return String(person.groupId) === targetId || String(person.group_id) === targetId;
  };

  // Get members of this group
  const members = useMemo(() => {
    const donorList = Object.entries(donors || {}).map(([name, data]) => ({
      name,
      ...data
    }));

    return donorList.filter(d => isPersonInGroup(d, group.id)).sort((a, b) => {
      const amtA = parseFloat(String(a.amount || '0').replace(/[^0-9.]/g, '')) || 0;
      const amtB = parseFloat(String(b.amount || '0').replace(/[^0-9.]/g, '')) || 0;
      if (amtB !== amtA) return amtB - amtA;
      return a.name.localeCompare(b.name);
    });
  }, [donors, group.id]);

  // Calculate pipeline total
  const pipelineTotal = useMemo(() => {
    return members.reduce((sum, d) => {
      const amt = parseFloat(String(d.amount || '0').replace(/[^0-9.]/g, '')) || 0;
      return sum + amt;
    }, 0);
  }, [members]);

  const formatPipeline = (amt) => {
    if (amt >= 1000000) return `$${(amt / 1000000).toFixed(1)}M`;
    if (amt >= 1000) return `$${Math.round(amt / 1000)}K`;
    if (amt > 0) return `$${amt}`;
    return '$0';
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const getPersonColor = (name) => {
    const colors = [
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#38bdf8', // sky blue
      '#a78bfa', // purple
      '#ec4899', // pink
      '#3b82f6', // blue
      '#14b8a6', // teal
      '#818cf8', // indigo
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // Show all members in the hero grid
  const heroMembers = members;

  return (
    <div className="group-members-panel">
      {/* Hero Section with Spotlight */}
      <div className="gmp-hero">
        <div className="gmp-spotlight" />
        <div className="gmp-spotlight secondary" />

        <div className="gmp-hero-content">
          {/* Header Row with Title and Tabs */}
          <div className="gmp-hero-header">
            <h1 className="gmp-hero-title">{group.name}</h1>

            {/* Toggle Tabs: Members, Lists, Tables */}
            <div className="gmp-fun-tabs">
              <button
                className={`gmp-fun-tab members ${activeTab === 'members' ? 'active' : ''}`}
                onClick={() => setActiveTab('members')}
              >
                <div className="gmp-fun-tab-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <span className="gmp-fun-tab-label">Members</span>
                {members.length > 0 && <span className="gmp-fun-tab-count">{members.length}</span>}
                <div className="gmp-fun-tab-shimmer" />
              </button>

              <button
                className={`gmp-fun-tab lists ${activeTab === 'lists' ? 'active' : ''}`}
                onClick={() => setActiveTab('lists')}
              >
                <div className="gmp-fun-tab-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"/>
                    <line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <circle cx="4" cy="6" r="1.5" fill="currentColor"/>
                    <circle cx="4" cy="12" r="1.5" fill="currentColor"/>
                    <circle cx="4" cy="18" r="1.5" fill="currentColor"/>
                  </svg>
                </div>
                <span className="gmp-fun-tab-label">Lists</span>
                {groupLists.length > 0 && <span className="gmp-fun-tab-count">{groupLists.length}</span>}
                <div className="gmp-fun-tab-shimmer" />
              </button>

              <button
                className={`gmp-fun-tab tables ${activeTab === 'tables' ? 'active' : ''}`}
                onClick={() => setActiveTab('tables')}
              >
                <div className="gmp-fun-tab-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="3"/>
                    <line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="3" y1="15" x2="21" y2="15"/>
                    <line x1="9" y1="3" x2="9" y2="21"/>
                    <line x1="15" y1="3" x2="15" y2="21"/>
                  </svg>
                </div>
                <span className="gmp-fun-tab-label">Tables</span>
                {groupTables.length > 0 && <span className="gmp-fun-tab-count">{groupTables.length}</span>}
                <div className="gmp-fun-tab-shimmer" />
              </button>
            </div>
          </div>

          <div className="gmp-hero-context-row">
            <p className="gmp-hero-context">
              {group.context
                ? (group.context.length > 150 ? group.context.slice(0, 150) + '...' : group.context)
                : `${members.length} ${members.length === 1 ? 'person' : 'people'}${pipelineTotal > 0 ? ` · ${formatPipeline(pipelineTotal)} pipeline` : ''}`}
            </p>
            <div className="gmp-hero-actions">
              <button className="gmp-add-person-btn" onClick={() => onAddPerson?.(group)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                Add Person
              </button>
              <button className="gmp-edit-context-btn" onClick={() => onEditContext?.(group)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit Context
              </button>
            </div>
          </div>

          {/* Member Cards - only show when no tab is active */}
          {activeTab === 'members' && heroMembers.length > 0 && (
            <div className="gmp-hero-members">
              {heroMembers.map((member, i) => {
                const hasAmount = member.amount && member.amount !== 'Unknown' && member.amount !== '$0';
                const nameParts = member.name.split(' ');
                const firstName = nameParts[0];
                const lastName = nameParts.slice(1).join(' ');
                // Diagonal animation: top-left to bottom-right
                const cols = 8;
                const row = Math.floor(i / cols);
                const col = i % cols;
                const diagonalDelay = (row + col) * 0.025;
                return (
                  <button
                    key={member.name}
                    className="gmp-hero-member-card"
                    onClick={() => onSelectPerson?.(member.name)}
                    style={{ animationDelay: `${diagonalDelay}s` }}
                  >
                    <div className="gmp-hero-member-avatar" style={{ borderColor: getPersonColor(member.name), color: getPersonColor(member.name) }}>
                      {getInitials(member.name)}
                    </div>
                    <div className="gmp-hero-member-name-stack">
                      <span className="gmp-hero-member-firstname">{firstName}</span>
                      {lastName && <span className="gmp-hero-member-lastname">{lastName}</span>}
                    </div>
                    {hasAmount && (
                      <span className="gmp-hero-member-amount">{member.amount}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'lists' && (
            <div className="gmp-tab-content lists">
              {groupLists.length === 0 ? (
                <div className="gmp-empty-fun">
                  <div className="gmp-empty-fun-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <line x1="8" y1="6" x2="21" y2="6"/>
                      <line x1="8" y1="12" x2="21" y2="12"/>
                      <line x1="8" y1="18" x2="21" y2="18"/>
                      <circle cx="4" cy="6" r="1.5"/>
                      <circle cx="4" cy="12" r="1.5"/>
                      <circle cx="4" cy="18" r="1.5"/>
                    </svg>
                  </div>
                  <p>No lists yet</p>
                  <span>Organize your contacts into smart lists</span>
                  <button className="gmp-create-btn" onClick={() => onCreateList?.(group)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                    Create List
                  </button>
                </div>
              ) : (
                <div className="gmp-items-grid">
                  <button
                    className="gmp-item-card add-new"
                    onClick={() => onCreateList?.(group)}
                  >
                    <div className="gmp-item-icon add">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                    </div>
                    <div className="gmp-item-name">New List</div>
                  </button>
                  {groupLists.map((list, i) => {
                    const listColors = ['#a78bfa', '#60a5fa', '#34d399', '#f472b6', '#fbbf24', '#22d3ee'];
                    const accentColor = listColors[i % listColors.length];
                    const totalPeople = list.groups?.reduce((sum, g) => sum + (g.people?.length || 0), 0) || 0;
                    const categoryPreviews = (list.groups || []).slice(0, 3);

                    return (
                      <button
                        key={list.id}
                        className="gmp-list-card"
                        onClick={() => onSelectList?.(list)}
                        style={{
                          animationDelay: `${(i + 1) * 0.08}s`,
                          '--accent-color': accentColor
                        }}
                      >
                        {onShowDeleteConfirm && (
                          <button
                            className="gmp-list-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              onShowDeleteConfirm('list', list, { top: rect.top - 50, left: rect.right + 8 });
                            }}
                            title="Delete list"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        )}
                        <div className="gmp-list-header">
                          <div className="gmp-list-icon" style={{ color: accentColor }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="8" y1="6" x2="21" y2="6"/>
                              <line x1="8" y1="12" x2="21" y2="12"/>
                              <line x1="8" y1="18" x2="21" y2="18"/>
                              <circle cx="4" cy="6" r="1.5" fill="currentColor"/>
                              <circle cx="4" cy="12" r="1.5" fill="currentColor"/>
                              <circle cx="4" cy="18" r="1.5" fill="currentColor"/>
                            </svg>
                          </div>
                          <div className="gmp-list-title">{list.name || list.organizationPrompt}</div>
                          <div className="gmp-list-count" style={{ background: `${accentColor}20`, color: accentColor }}>
                            {totalPeople}
                          </div>
                        </div>
                        {categoryPreviews.length > 0 && (
                          <div className="gmp-list-categories">
                            {categoryPreviews.map((cat, ci) => (
                              <div key={ci} className="gmp-list-category">
                                <span className="gmp-list-category-dot" style={{ background: accentColor }} />
                                <span className="gmp-list-category-name">{cat.name}</span>
                                <span className="gmp-list-category-count">{cat.people?.length || 0}</span>
                              </div>
                            ))}
                            {(list.groups || []).length > 3 && (
                              <div className="gmp-list-category more">
                                +{(list.groups || []).length - 3} more
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'tables' && (
            <div className="gmp-tab-content tables">
              {groupTables.length === 0 ? (
                <div className="gmp-empty-fun">
                  <div className="gmp-empty-fun-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="3"/>
                      <line x1="3" y1="9" x2="21" y2="9"/>
                      <line x1="9" y1="3" x2="9" y2="21"/>
                    </svg>
                  </div>
                  <p>No tables yet</p>
                  <span>Create tables to organize data about your contacts</span>
                  <button className="gmp-create-btn" onClick={() => onCreateTable?.(group)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                    Create Table
                  </button>
                </div>
              ) : (
                <div className="gmp-items-grid">
                  <button
                    className="gmp-item-card add-new"
                    onClick={() => onCreateTable?.(group)}
                  >
                    <div className="gmp-item-icon add">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                    </div>
                    <div className="gmp-item-name">New Table</div>
                  </button>
                  {groupTables.map((table, i) => {
                    const tableColors = ['#22d3ee', '#a78bfa', '#34d399', '#f472b6', '#fbbf24', '#60a5fa'];
                    const accentColor = tableColors[i % tableColors.length];
                    const columnPreviews = (table.columns || []).filter(c => c.toLowerCase() !== 'name').slice(0, 3);
                    const rowCount = table.rows?.length || 0;

                    return (
                      <button
                        key={table.id}
                        className="gmp-list-card table-card"
                        onClick={() => onSelectTable?.(table)}
                        style={{
                          animationDelay: `${(i + 1) * 0.08}s`,
                          '--accent-color': accentColor,
                          background: 'transparent',
                          border: `3px solid ${accentColor}`
                        }}
                      >
                        {onShowDeleteConfirm && (
                          <button
                            className="gmp-list-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              onShowDeleteConfirm('table', table, { top: rect.top - 50, left: rect.right + 8 });
                            }}
                            title="Delete table"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        )}
                        <div className="gmp-list-header">
                          <div className="gmp-list-icon" style={{ color: accentColor }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="3"/>
                              <line x1="3" y1="9" x2="21" y2="9"/>
                              <line x1="3" y1="15" x2="21" y2="15"/>
                              <line x1="9" y1="3" x2="9" y2="21"/>
                              <line x1="15" y1="3" x2="15" y2="21"/>
                            </svg>
                          </div>
                          <div className="gmp-list-title">{table.name || 'Untitled Table'}</div>
                          <div className="gmp-list-count" style={{ background: `${accentColor}20`, color: accentColor }}>
                            {rowCount}
                          </div>
                        </div>
                        {columnPreviews.length > 0 && (
                          <div className="gmp-list-categories">
                            {columnPreviews.map((col, ci) => (
                              <div key={ci} className="gmp-list-category">
                                <span className="gmp-list-category-dot" style={{ background: accentColor }} />
                                <span className="gmp-list-category-name">{col}</span>
                              </div>
                            ))}
                            {(table.columns || []).filter(c => c.toLowerCase() !== 'name').length > 3 && (
                              <div className="gmp-list-category more">
                                +{(table.columns || []).filter(c => c.toLowerCase() !== 'name').length - 3} more
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DesktopGroupView;
